import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ModalOverlay from '../ModalOverlay/ModalOverlay.tsx';
import { openMainModal } from '../../../store/actions/profileActions';
import { generateProfilePostText, createProfilePost } from '../../../store/actions';
import styles from './ProfilePostSuggestionModal.module.scss';

interface ProfilePostSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish?: () => void;
  userData?: any;
}

const ProfilePostSuggestionModal: React.FC<ProfilePostSuggestionModalProps> = ({
  isOpen,
  onClose,
  onPublish,
  userData,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [suggestedText, setSuggestedText] = useState<string>('');
  const [editedText, setEditedText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    if (!isOpen) {
      setContentHeight(0);
      return;
    }
    let ro: ResizeObserver | null = null;
    const frameId = requestAnimationFrame(() => {
      const el = contentRef.current;
      if (!el) return;
      ro = new ResizeObserver((entries) => {
        const height = entries[0]?.contentRect.height ?? 0;
        setContentHeight((prev) => (height > 0 ? height : prev));
      });
      ro.observe(el);
    });
    return () => {
      cancelAnimationFrame(frameId);
      ro?.disconnect();
    };
  }, [isOpen, isLoading, suggestedText]);

  useEffect(() => {
    if (isOpen && !suggestedText) {
      fetchSuggestedText();
    }
    if (!isOpen) {
      setSuggestedText('');
      setEditedText('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (suggestedText && !editedText) {
      setEditedText(suggestedText);
    }
  }, [suggestedText]);

  const getSkillName = (skill: any): string => {
    if (typeof skill === 'string') {
      return skill;
    }
    if (skill?.skill?.name) {
      return skill.skill.name;
    }
    if (skill?.name) {
      return skill.name;
    }
    return '';
  };

  const hasOnlyOtrosAsSkill = (userData: any): boolean => {
    if (!userData?.skills?.length) return false;
    const skillNames = userData.skills.map(getSkillName).filter((s: string) => s && s.trim() !== '');
    return skillNames.length > 0 && skillNames.every((s: string) => s.trim().toLowerCase() === 'otros');
  };

  const getGenericIncompleteProfileText = (): string => {
    return '¡Hola! Estoy en Edify y me encantaría conectarme con profesionales de la construcción.\n\nSi necesitás colaboración en proyectos, sumar gente a tu equipo o conocer otros del rubro, escribime.';
  };

  const generateGenericText = (userData: any): string => {
    if (!userData) {
      return getGenericIncompleteProfileText();
    }
    if (hasOnlyOtrosAsSkill(userData)) {
      return getGenericIncompleteProfileText();
    }

    const isCompany = userData.type === 'COMPANY';
    let name = '';
    if (isCompany) {
      name = userData.razon_social || userData.username || 'Mi empresa';
    } else {
      const firstName = userData.name || '';
      const lastName = userData.last_name || '';
      name = `${firstName} ${lastName}`.trim() || userData.username || 'Yo';
    }
    const description = userData.description || '';
    const skills = userData.skills || [];
    const validSkills = skills
      .map(getSkillName)
      .filter((s: string) => s && s.trim() !== '' && s.trim().toLowerCase() !== 'otros');
    const skillNames = validSkills.slice(0, 3).join(', ');

    let text = `¡Hola! Soy ${name}`;
    if (isCompany) {
      text += ', una empresa';
    } else {
      text += ', un profesional';
    }
    text += '.\n\n';
    if (description && description.trim()) {
      text += `${description.trim()}\n\n`;
    }
    if (skillNames) {
      text += `Me especializo en ${skillNames}`;
      if (validSkills.length > 3) {
        text += ` y más`;
      }
      text += '.\n\n';
    }
    text += 'Estoy en Edify para conectarme con otros profesionales y proyectos. ¡Contáctame si necesitás mis servicios!';
    return text;
  };

  const formatTextWithLineBreaks = (text: string): string => {
    if (!text) return '';
    
    let normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    if (normalizedText.includes('\n')) {
      return normalizedText;
    }
    
    const sentences = normalizedText.split(/(?<=[.!?])\s+(?=[A-Z¿¡])/);
    
    if (sentences.length <= 2) {
      return normalizedText;
    }
    
    const paragraphs: string[] = [];
    let currentParagraph = '';
    
    sentences.forEach((sentence, index) => {
      const trimmed = sentence.trim();
      if (!trimmed) return;
      
      currentParagraph = currentParagraph ? currentParagraph + ' ' + trimmed : trimmed;
      
      if (index === sentences.length - 1) {
        paragraphs.push(currentParagraph);
      } else if (index > 0 && (index + 1) % 2 === 0) {
        paragraphs.push(currentParagraph);
        currentParagraph = '';
      }
    });
    
    return paragraphs.join('\n\n');
  };

  const fetchSuggestedText = async () => {
    setIsLoading(true);
    try {
      const text = await dispatch(generateProfilePostText() as any);
      const formattedText = formatTextWithLineBreaks(text);
      setSuggestedText(formattedText);
      setEditedText(formattedText);
    } catch (err: any) {
      const genericText = generateGenericText(userData);
      setSuggestedText(genericText);
      setEditedText(genericText);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!editedText.trim()) {
      dispatch(openMainModal({
        title: 'Texto vacío',
        body: 'El texto no puede estar vacío. Escribí algo para publicar.',
        iconType: 'error',
        isSuccess: false,
        confirmHandler: null,
      }));
      return;
    }

    setIsPublishing(true);
    try {
      await dispatch(createProfilePost({
        content: editedText.trim(),
        images: '',
        video: '',
      }) as any);
      onClose();
      if (onPublish) {
        onPublish();
      } else {
        navigate('/marketplace/explorar');
      }
    } catch (err: any) {
      dispatch(openMainModal({
        title: 'Error al publicar',
        body: 'No pudimos publicar el posteo. Intentá nuevamente en unos minutos.',
        iconType: 'error',
        isSuccess: true,
        confirmHandler: null,
      }));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCancel = () => {
    setEditedText(suggestedText);
    onClose();
  };

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleCancel} preventClose={isPublishing || isLoading}>
      <div className={styles.modal}>
        <h3 className={styles.title}>Presentate en la plataforma</h3>
        <p className={styles.description}>
          Generamos un texto sugerido para que te presentes. Es editable y muy útil para que otros usuarios te conozcan.
        </p>
        <div
          className={styles.contentBodyWrapper}
          style={{ height: contentHeight }}
        >
          <div ref={contentRef} className={styles.contentBody}>
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p className={styles.loadingText}>Generando texto sugerido...</p>
              </div>
            ) : (
              <>
                <textarea
                  className={styles.textarea}
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  placeholder="Escribí tu presentación..."
                  rows={8}
                  disabled={isPublishing}
                />
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={handleCancel}
                    disabled={isPublishing || isLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className={styles.publishButton}
                    onClick={handlePublish}
                    disabled={isPublishing || isLoading || !editedText.trim()}
                  >
                    {isPublishing ? 'Publicando...' : 'Publicar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default ProfilePostSuggestionModal;
